<template>
  <div :class="classes(n())">
    <Picker
      ref="pickerRef"
      :model-value="internalValues"
      :value="internalValues"
      :visible="visible"
      :columns="dateColumns"
      :title="title"
      :confirm-button-text="confirmButtonText"
      :show-close="showClose"
      :close-on-click-overlay="closeOnClickOverlay"
      :disabled="disabled"
      :teleport="teleport"
      :lock-scroll="lockScroll"
      :destroy-on-close="destroyOnClose"
      :visible-option-num="visibleOptionNum"
      :option-height="optionHeight"
      :swipe-duration="swipeDuration"
      @update:visible="handleVisibleChange"
      @confirm="handleConfirm"
      @cancel="handleCancel"
      @change="handleChange"
    >
      <template
        v-if="$slots.toolbar"
        #toolbar
      >
        <slot name="toolbar" />
      </template>
      <template
        v-if="$slots.title"
        #title
      >
        <slot name="title" />
      </template>
      <template
        v-if="$slots.option"
        #option="slotProps"
      >
        <slot
          name="option"
          v-bind="slotProps"
        />
      </template>
      <template
        v-if="$slots['columns-top']"
        #columns-top
      >
        <slot name="columns-top" />
      </template>
      <template
        v-if="$slots['columns-bottom']"
        #columns-bottom
      >
        <slot name="columns-bottom" />
      </template>
      <template
        v-if="$slots.confirm"
        #confirm
      >
        <slot name="confirm" />
      </template>
    </Picker>
  </div>
</template>

<script lang="ts">
import {
  ref,
  computed,
  watch,
  defineComponent,
  isVue3,
} from 'virtual-vue';
import { props } from './props';
import { emits } from './emits';
import { createNamespace } from '../utils/components';
import { clamp } from '../shared/number';
import Picker from '../picker/picker.vue';
import { useDateColumns } from './use-date-columns';
import { useDateFormat } from './use-date-format';
import type { PickerExpose, PickerEventParams, PickerChangeEventParams } from '../picker/types';
import type { DatePickerColumnType } from './types';

const { name, n, classes } = createNamespace('date-picker');

export default defineComponent({
  name,
  components: {
    Picker,
  },
  props,
  emits,
  setup(props, { emit }) {
    /** Picker 组件实例引用，用于调用 confirm 等方法 */
    const pickerRef = ref<PickerExpose | null>(null);

    // ==================== 格式化与 v-model 兼容层 ====================

    const {
      getInputMode,
      formatValues,
      normalizeToArray,
      getModelValue,
      arrayToDate,
    } = useDateFormat({
      columnsType: () => props.columnsType,
      format: () => props.format,
      modelValue: () => props.modelValue,
      value: () => (props as Record<string, unknown>).value as string[] | string | Date,
    });

    /** 向外 emit v-model 值变更，根据用户传入格式返回对应类型 */
    const emitModelValue = (val: string[]) => {
      const mode = getInputMode();
      let output: string[] | string | Date;
      if (mode === 'date') {
        output = arrayToDate(val);
      } else if (mode === 'string') {
        output = formatValues(val);
      } else {
        output = val;
      }
      emit('update:modelValue', output);
      emit('input', output);
    };

    // ==================== 内部状态 ====================

    /** 内部选中值副本，与列类型一一对应（如 ['2024', '06', '15']） */
    const internalValues = ref<string[]>([...getModelValue()]);

    /**
     * 初始化日期列 composable，获得：
     * - dateColumns：响应式的二维列选项数据，直接传给 Picker
     * - correctValues：值修正函数，将越界值调整到合法范围
     */
    const { columns: dateColumns, correctValues } = useDateColumns({
      columnsType: computed(() => props.columnsType),
      minDate: computed(() => props.minDate),
      maxDate: computed(() => props.maxDate),
      currentValues: internalValues,
      formatter: computed(() => props.optionsFormatter),
      filter: computed(() => props.filter),
    });

    // ==================== 默认值与校验 ====================

    /**
     * 生成基于当前时间的默认值，将 now 钳位到 [minDate, maxDate] 范围内，
     * 再按 columnsType 提取对应字段。用于 v-model 为空时的兜底。
     */
    function getDefaultValues(): string[] {
      const now = new Date();
      const min = props.minDate;
      const max = props.maxDate;

      const clampedDate = new Date(
        clamp(now.getTime(), min.getTime(), max.getTime()),
      );

      const types = props.columnsType;
      return types.map((type: DatePickerColumnType) => {
        if (type === 'year') return String(clampedDate.getFullYear());
        if (type === 'month') return String(clampedDate.getMonth() + 1).padStart(2, '0');
        return String(clampedDate.getDate()).padStart(2, '0');
      });
    }

    /** 确保值数组有效：为空时生成默认值，否则通过 correctValues 修正越界项 */
    function ensureValidValues(vals: string[]): string[] {
      if (!vals || vals.length === 0) {
        return getDefaultValues();
      }
      return correctValues(vals);
    }

    // ==================== 事件处理 ====================

    /** visible 变化透传给父组件，支持 .sync / v-model:visible */
    const handleVisibleChange = (val: boolean) => {
      emit('update:visible', val);
    };

    /** 确认按钮：将内部值同步到 v-model 并 emit confirm 事件 */
    const handleConfirm = (params: PickerEventParams) => {
      const confirmedValues = [...internalValues.value];
      emitModelValue(confirmedValues);
      emit('confirm', {
        ...params,
        selectedValues: confirmedValues,
        formattedValue: formatValues(confirmedValues),
      });
    };

    /** 取消按钮：直接透传 cancel 事件 */
    const handleCancel = (params: PickerEventParams) => {
      emit('cancel', params);
    };

    /**
     * 列滚动选中值变化时触发。
     *
     * 当变化的是 year 或 month 列时，后续列的可选范围可能改变（如 2 月天数、
     * 边界年的月份范围），需要同步修正所有列的值以保证一致性。
     * 修正在当前 tick 内同步完成，避免与 Picker 子组件的 watch 产生竞态。
     */
    const handleChange = (params: PickerChangeEventParams) => {
      const { columnIndex } = params;
      const newValues = params.selectedValues.map(String);

      const types = props.columnsType;
      const changedType = types[columnIndex] as DatePickerColumnType;

      if (changedType === 'year' || changedType === 'month') {
        const corrected = correctValues(newValues);
        internalValues.value = corrected;
      } else {
        internalValues.value = newValues;
      }

      emit('change', {
        ...params,
        selectedValues: [...internalValues.value],
        formattedValue: formatValues([...internalValues.value]),
        columnIndex,
      });
    };

    // ==================== 外部值 → 内部同步 ====================

    /** 外部 v-model 变化时同步到 internalValues，同时校验合法性 */
    watch(
      () => (isVue3 ? props.modelValue : (props as Record<string, unknown>).value as string[] | string | Date),
      (raw: string[] | string | Date) => {
        const val = normalizeToArray(raw);
        if (JSON.stringify(val) !== JSON.stringify(internalValues.value)) {
          internalValues.value = val.length > 0
            ? ensureValidValues([...val])
            : [...val];
        }
      },
    );

    /** 弹窗打开时，从外部 v-model 重新读取值并校验（用户可能在关闭期间修改了绑定值） */
    watch(
      () => props.visible,
      (val) => {
        if (val) {
          const modelVal = getModelValue();
          internalValues.value = ensureValidValues(
            modelVal.length > 0 ? [...modelVal] : [],
          );
        }
      },
    );

    /** minDate / maxDate 动态变化时，重新校验并修正当前值 */
    watch(
      [() => props.minDate, () => props.maxDate],
      () => {
        if (internalValues.value.length > 0) {
          const corrected = correctValues(internalValues.value);
          if (JSON.stringify(corrected) !== JSON.stringify(internalValues.value)) {
            internalValues.value = corrected;
            emitModelValue([...corrected]);
          }
        }
      },
    );

    // ==================== 暴露给外部的方法 ====================

    /** 打开选择器弹窗 */
    const open = () => {
      emit('update:visible', true);
    };
    /** 关闭选择器弹窗 */
    const close = () => {
      emit('update:visible', false);
    };
    /** 等同于点击确认按钮 */
    const confirm = () => {
      pickerRef.value?.confirm();
    };
    /** 获取当前内部选中的日期值数组 */
    const getSelectedDate = (): string[] => [...internalValues.value];

    return {
      n,
      classes,
      pickerRef,
      internalValues,
      dateColumns,
      handleVisibleChange,
      handleConfirm,
      handleCancel,
      handleChange,
      open,
      close,
      confirm,
      getSelectedDate,
    };
  },
});
</script>
